import React, {useEffect, useRef, useState} from "react";
import {Navigate, useLocation, useNavigate} from "react-router-dom";
import {PstatCmntModel, PstatModel} from "app/model/info/pstat";
import ArcDownload, {dataSourceProps} from "app/components/arc/ArcDownload";
import {FileInfoModel} from "app/model/common/file";
import moment from "moment";
import {Constant} from "app/constants/constant";
import {useTranslation} from "react-i18next";
import {showAndHideSpinner} from "app/reducers/spinnerSlice";
import {FileService} from "app/services/common/file";
import { useAppDispatch, useAppSelector } from "app/store/hook";
import ArcFileUploader, {dbFileInfo} from "app/components/arc/ArcFileUploader";
import {BltnbClCd, CrudConstant, PrstsCd} from "app/model/common/code";
import ArcButton, {ButtonType} from "app/components/arc/ArcButton";
import ObjectUtil from "app/utils/objectUtil";
import {toast} from "react-toastify";
import {PstatService} from "app/services/info/pstatService";
import {ResponseResultCode} from "app/model/response-api";
import ArcEditor from "app/components/arc/ArcEditor";
import ArcEditorView from "app/components/arc/ArcEditorView";
import {openModal} from "app/reducers/modalSlice";
import AppAlert from "app/components/common/AppAlert";
import ArcGrid from "../../../../components/arc/ArcGrid";
import ArcTitle from "../../../../components/arc/ArcTitle";
import { UserInfoUtil } from "app/utils/userInfoUtil";

export default function Info004WriteAsk() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const userInfo = useAppSelector(state => state.userInfo.userInfo);

  if (!UserInfoUtil.isAdmin(userInfo)) {
    return <Navigate to="/not-permission" replace />;
  }

  if (!location.state) {
    return <Navigate to="/invalid-access" replace />;
  }

  // Pstat data
  const [pstat,] = useState<PstatModel>(location.state ?? new PstatModel(BltnbClCd.API, PrstsCd.PROCESS));
  const [pstatFiles, setPstatFiles] = useState<dataSourceProps[]>([]);
  const [status, setStatus] = useState<string>();
  const [statusClass, setStatusClass] = useState<string>();
  const editorRef: any = useRef<typeof ArcGrid>();

  useEffect(() => {
    if (PrstsCd.PROCESS === pstat.prstsCd) {
      if (pstat.cmntList && pstat.cmntList.length > 0) {
        setStatus(t('info.code.prstsCd.PROGRESS').toString());
        setStatusClass('p-badge2');
      } else {
        setStatus(t(`info.code.prstsCd.${pstat.prstsCd}`).toString());
        setStatusClass('p-badge5');
      }
    } else {
      setStatus(t(`info.code.prstsCd.${pstat.prstsCd}`).toString());
      setStatusClass('p-badge1');
    }
  }, [pstat]);

  // Cmnt data
  const initialCmntModel = () => {
    if (location.state && location.state.cmntList && location.state.cmntList.length > 0) {
      return location.state.cmntList[0];
    }
    return new PstatCmntModel(pstat.pstatId ?? "");
  }
  const [model, setModel] = useState<PstatCmntModel>(initialCmntModel);
  const initialCmntFiles = () => {
    let files = model.file?.files ?? [];
    if (files.length > 0) {
      return files.map((m: FileInfoModel): dbFileInfo => {
        return {name: m.realNm ?? '', size: m.size ?? 0, fileId: m.fileId ?? ''};
      });
    } else {
      return [];
    }
  }
  const [dataFiles,] = useState(initialCmntFiles);
  const [newFiles, setNewFiles] = useState<File[]>([]);

  useEffect(() => {
    const files = pstat.file?.files ?? [];
    if (files.length > 0) {
      const downloads = files.map((f: FileInfoModel): dataSourceProps => {
        return {realName: f.realNm ?? "", saveName: f.fileNm ?? ""}
      });
      setPstatFiles(downloads);
    } else {
      setPstatFiles([]);
    }
  }, []);

  async function handleClickDownload(selectFile: dataSourceProps) {
    const dataFile = model.file?.files ?? [];
    if (dataFile && dataFile.length > 0) {
      const selectedFile = dataFile.find(f => f.fileNm === selectFile.saveName);
      if (selectedFile) {
        dispatch(showAndHideSpinner(true));
        await FileService.getInstance().downloadFile(process.env.REACT_APP_URL_WP ?? '', selectedFile);
        dispatch(showAndHideSpinner(false));
      }
    }
  }

  function handleExistFile(files: any[]) {
    const deletedFiles: any[] = dataFiles.filter((f: any) => !files.includes(f));
    let formFiles = model.file?.files?.map((m: FileInfoModel): FileInfoModel => {
      if (deletedFiles.findIndex((f: any) => f.fileId === m.fileId) >= 0) {
        return {...m, crud: CrudConstant.D};
      }
      return {...m};
    });
    setModel({
      ...model,
      file: {
        ...model.file,
        files: formFiles
      }
    });
  }

  function handleClickFileAdd(files: File[]) {
    setNewFiles(files);
  }

  function handleChangeEditor(content: string) {
    setModel({
      ...model,
      cmntCtt: content,
    });
  }

  function handleClickGoBack() {
    navigate(-1);
  }

  async function handleClickDoSave() {
    if (validationForm(model)) {
      const formData: FormData = new FormData();
      const data = JSON.stringify(model);
      formData.append('data', data);
      if (newFiles.length > 0) {
        newFiles.forEach(f => formData.append('files', f));
      }

      dispatch(openModal({
        path: '/info/api/info004/cmnt/form/save/confirm',
        target: AppAlert,
        confirmProps: {
          type: 'save',
          onConfirm: () => {
            dispatch(showAndHideSpinner(true));
            PstatService.getInstance().saveCmnt(formData)
            .then(response => {
              dispatch(showAndHideSpinner(false));
              if (response && ResponseResultCode.Y === response.resultCode) {
                const param = {...pstat, cmntList: [response.data]};
                toast.success(t('common.message.success.save', {title: ''}));
                navigate('/info/api/info004/detail', {state: param, replace: true});
              }
            })
            .catch(() => dispatch(showAndHideSpinner(false)));
          }
        }
      }));
    }
  }

  function validationForm(param: PstatCmntModel) {
    let check: boolean = true;

    if (ObjectUtil.isEmpty(param.cmntCtt)) {
      check = false;
    }

    if (!check) {
      toast.error(t('common.message.error.checkRequired'));
      setModel((prev: PstatCmntModel) => {
        return {
          ...prev,
          cmntCtt: prev.cmntCtt ?? "",
        }
      });
    }
    return check;
  }

  return (
    <div id="container">
      <div className="sub-wrap">
        <ArcTitle title="API 요청 게시판" />

        <div className="col-wrap">
          <div className="col-md-12">
            <div className="cont">
              <div className="tbl-st-row">
                <table>
                  <caption>API 답변 등록</caption>
                  <colgroup>
                    <col style={{ width: '100%' }} />
                  </colgroup>
                  <thead>
                  <tr>
                    <th className="pdt20 pdb10 al" style={{ backgroundColor: 'transparent'}}>
                      <h3 className="titT1">{pstat.titlNm}</h3>
                      <ul className="posting-status">
                        <li>{pstat.regrNm}</li>
                        <li>
                          {pstat.regDate ? moment(pstat.regDate).format(Constant.FORMAT_YMD) : ''}
                        </li>
                        <li>
                          <em className={statusClass}>{status ?? ''}</em>
                        </li>
                      </ul>
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr>
                    <td className="pdt20 pdb20">
                      <ArcEditorView value={pstat.pstatCtt ?? ""}/>
                    </td>
                  </tr>
                  <tr>
                    <td className="pdt10 pdb10">
                      <ArcDownload dataSource={pstatFiles}
                                   onDownload={handleClickDownload}/>
                    </td>
                  </tr>
                  <tr>
                    <td className="pdt15 pdb15 pdl0 pdr0">
                      <ArcEditor id="cmntCtt"
                                 height="15rem"
                                 editorRef={editorRef}
                                 value={model.cmntCtt}
                                 onChange={handleChangeEditor}
                                 savedPagePath="/info/api/info004/detail"
                                 imgServerPath={process.env.REACT_APP_URL_WP ?? ""}/>
                    </td>
                  </tr>
                  {/*<tr>*/}
                  {/*  <td className="pdt15 pdb15 pdl0 pdr0">*/}
                  {/*    <ArcFileUploader maxFileNum={5}*/}
                  {/*                     maxFileSizeMb={90}*/}
                  {/*                     dbFileList={dataFiles}*/}
                  {/*                     dbUpload={handleExistFile}*/}
                  {/*                     onUpload={handleClickFileAdd}/>*/}
                  {/*  </td>*/}
                  {/*</tr>*/}
                  </tbody>
                </table>
              </div>

              <div className="btns ar">
                <ArcButton type={ButtonType.save}
                           hasIcon={true}
                           onClick={handleClickDoSave}/>
                <ArcButton type={ButtonType.cancel}
                           onClick={handleClickGoBack}/>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
